import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useMemo, useRef } from "react";
import { api } from "@/lib/api";
import { Search, Command, ArrowRight, LayoutDashboard, Users2, Users, Package, ShoppingCart, Award, Tag, Bell, ShieldAlert, TrendingUp, FileText, Sparkles, Wallet, Layers, Menu, Building2, TrendingDown } from "lucide-react";
import NotificationBell from "./NotificationBell";
import ConnectionBadge from "./ConnectionBadge";
import ActivityFeed from "./ActivityFeed";
import ThemeToggle from "./ThemeToggle";
import { useAuth } from "@/context/AuthContext";

/**
 * Beem — Top bar with:
 *  - breadcrumb / page title
 *  - global "spotlight" search (Cmd/Ctrl-K) with keyword-to-route mapping,
 *    keyboard navigation (↑↓, Enter) and results grouped by category.
 *  - notification bell dropdown
 *  - team activity feed dropdown (director only)
 *  - theme toggle
 */

const PAGE_META = {
  "/": ["Panel", "Vista general del negocio"],
  "/director-comercial": ["Director Comercial", "Reportes generados por IA"],
  "/pedidos": ["Pedidos", "Ventas y órdenes"],
  "/pagos": ["Pagos", "Comprobantes y aprobaciones"],
  "/excepciones": ["Excepciones", "Aprobaciones de precio"],
  "/clientes": ["Clientes", "Directorio comercial"],
  "/productos": ["Productos", "Catálogo e inventario"],
  "/variantes": ["Detección de variantes", "Agrupar SKUs similares"],
  "/comisiones": ["Reglas de comisión", "Cómo gana cada venta"],
  "/reportes": ["Reportes", "Actividad y comisiones"],
  "/listas-precios": ["Políticas comerciales", "Listas de precios por segmento"],
  "/notificaciones": ["Notificaciones", "Historial de alertas"],
  "/remisiones": ["Remisiones OCR", "Subir fotos de remisiones"],
  "/equipo": ["Equipo", "Miembros y permisos"],
  "/empresa": ["Datos de empresa", "Logo, NIT y contacto para PDFs"],
  "/panel-stock": ["Panel bajo stock", "Productos próximos a agotarse"],
  "/mis-comisiones": ["Mis comisiones", "Detalle privado por pedido"],
};

// Módulos navegables desde el buscador. Cada entrada tiene sinónimos.
const MODULES = [
  { path: "/", label: "Panel", desc: "Vista general del negocio", icon: LayoutDashboard, adminOnly: false,
    keywords: ["panel", "dashboard", "inicio", "home", "principal", "resumen"] },
  { path: "/director-comercial", label: "Director Comercial", desc: "Reportes con IA", icon: Sparkles, adminOnly: true,
    keywords: ["director", "director comercial", "ia", "ai", "insights"] },
  { path: "/equipo", label: "Equipo", desc: "Miembros y permisos", icon: Users2, adminOnly: true,
    keywords: ["equipo", "team", "usuarios", "personal", "colaboradores", "empleados", "miembros", "permisos", "roles"] },
  { path: "/clientes", label: "Clientes", desc: "Directorio comercial", icon: Users, adminOnly: false,
    anyOf: ["clientes.crear", "clientes.editar", "ventas.crear", "ventas.ver_todos"],
    keywords: ["clientes", "cliente", "customers", "directorio", "contactos"] },
  { path: "/productos", label: "Productos", desc: "Catálogo e inventario", icon: Package, adminOnly: false,
    keywords: ["productos", "producto", "catalogo", "catálogo", "inventario", "stock", "insumos", "referencias", "skus"] },
  { path: "/variantes", label: "Detección de variantes", desc: "Agrupar SKUs similares", icon: Layers, adminOnly: true,
    keywords: ["variantes", "variante", "detectar", "agrupar", "grupos", "similares", "duplicados", "unificar"] },
  { path: "/pedidos", label: "Pedidos", desc: "Ventas y órdenes", icon: ShoppingCart, adminOnly: false,
    keywords: ["pedidos", "pedido", "ventas", "ordenes", "órdenes", "facturas", "sales"] },
  { path: "/pagos", label: "Pagos", desc: "Comprobantes y aprobaciones", icon: Wallet, adminOnly: false,
    anyOf: ["pagos.reportar", "pagos.aprobar"],
    keywords: ["pagos", "pago", "comprobantes", "comprobante", "tesoreria", "tesorería", "cobros", "cobranzas", "abonos", "consignaciones", "recibos"] },
  { path: "/comisiones", label: "Reglas de comisión", desc: "Motor comercial", icon: Award, adminOnly: true,
    keywords: ["comisiones", "comisión", "comision", "reglas", "comisiones vendedores", "porcentaje"] },
  { path: "/listas-precios", label: "Políticas comerciales", desc: "Listas de precios", icon: Tag, adminOnly: true,
    keywords: ["listas", "listas de precios", "políticas", "politicas", "precios", "segmentos", "tarifas"] },
  { path: "/excepciones", label: "Excepciones", desc: "Aprobar solicitudes de precio", icon: ShieldAlert, adminOnly: false,
    anyOf: ["excepciones.aprobar"],
    keywords: ["excepciones", "excepción", "aprobaciones", "precios especiales", "descuentos"] },
  { path: "/notificaciones", label: "Notificaciones", desc: "Historial de alertas", icon: Bell, adminOnly: false,
    keywords: ["notificaciones", "alertas", "avisos", "bell", "campana"] },
  { path: "/reportes", label: "Reportes", desc: "Actividad y comisiones", icon: TrendingUp, adminOnly: false,
    anyOf: ["ventas.crear", "comisiones.ver", "reportes.exportar"],
    keywords: ["reportes", "reporte", "informes", "informe", "analytics", "estadisticas", "estadísticas", "métricas"] },
  { path: "/remisiones", label: "Remisiones OCR", desc: "Subir fotos de remisiones", icon: FileText, adminOnly: false,
    anyOf: ["ventas.crear", "ventas.ver_todos"],
    keywords: ["remisiones", "remisión", "ocr", "fotos", "escanear", "escaneo"] },
  { path: "/empresa", label: "Datos de empresa", desc: "Logo, NIT y contacto para PDFs", icon: Building2, adminOnly: true,
    keywords: ["empresa", "compañia", "compañía", "nit", "logo", "datos", "razon social", "razón social", "contacto"] },
  { path: "/panel-stock", label: "Panel bajo stock", desc: "Productos próximos a agotarse", icon: TrendingDown, adminOnly: true,
    keywords: ["bajo stock", "stock", "agotar", "reorden", "pedido", "china", "inventario bajo", "reordenar", "reponer"] },
  { path: "/mis-comisiones", label: "Mis comisiones", desc: "Detalle privado de tus comisiones", icon: Award, adminOnly: false,
    hideForAdmin: true, anyOf: ["ventas.crear", "comisiones.ver"],
    keywords: ["mis comisiones", "comisión", "comision", "ganancia", "ganado", "mi bono", "cuánto he ganado"] },
];

function matchesModule(mod, query) {
  const q = query.toLowerCase().trim();
  if (!q) return false;
  if (mod.label.toLowerCase().includes(q)) return true;
  if (mod.desc.toLowerCase().includes(q)) return true;
  // Solo match si el keyword completo empieza con el query, o el query es un prefijo del keyword.
  // Evita que "ia" matchee "var[ia]ntes" o keywords cortos peguen en cualquier palabra.
  return mod.keywords.some((k) => {
    if (k.length < 3) return k === q;
    return k.startsWith(q) || (q.length >= 3 && k.includes(q));
  });
}

export default function TopBar({ onOpenMobileNav }) {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState({ productos: [], clientes: [], pedidos: [] });
  const [loading, setLoading] = useState(false);
  const [cursor, setCursor] = useState(0);
  const listRef = useRef(null);

  const isAdmin = user?.role === "admin";

  const [title, subtitle] = PAGE_META[location.pathname] || [
    location.pathname.split("/").filter(Boolean).map((s) => s[0].toUpperCase() + s.slice(1)).join(" › ") || "Panel",
    "",
  ];

  // Cmd/Ctrl+K to open, ESC to close
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen(true);
        setTimeout(() => document.getElementById("beem-spotlight-input")?.focus(), 30);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Debounced backend fetch for productos/clientes/pedidos
  useEffect(() => {
    if (!q || q.length < 2) { setResults({ productos: [], clientes: [], pedidos: [] }); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const [p, c, pd] = await Promise.all([
          api.get(`/productos?q=${encodeURIComponent(q)}&limit=6`).then(r => r.data).catch(() => []),
          api.get(`/clientes?q=${encodeURIComponent(q)}&limit=6`).then(r => r.data).catch(() => []),
          api.get(`/pedidos?q=${encodeURIComponent(q)}&limit=6`).then(r => r.data).catch(() => []),
        ]);
        setResults({
          productos: (p || []).slice(0, 6),
          clientes: (c || []).slice(0, 6),
          pedidos: (pd || []).slice(0, 6),
        });
      } finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  // Filtered modules (fast, local) — ranked so that label-startsWith wins
  const modulesFound = useMemo(() => {
    if (!q || q.length < 1) return [];
    const perms = user?.permissions || [];
    const bypass = isAdmin || perms.includes("permisos.administrar");
    const hasAnyOf = (list) => !list || list.length === 0 || bypass || list.some((p) => perms.includes(p));
    const ql = q.toLowerCase().trim();
    return MODULES
      .filter((m) => !m.adminOnly || isAdmin)
      .filter((m) => !(m.hideForAdmin && isAdmin))
      .filter((m) => hasAnyOf(m.anyOf))
      .filter((m) => matchesModule(m, q))
      .map((m) => {
        // Score: label startsWith → 0; label contains → 1; keyword startsWith → 2; keyword contains → 3; desc → 4
        const lbl = m.label.toLowerCase();
        if (lbl.startsWith(ql)) return { m, score: 0 };
        if (lbl.includes(ql)) return { m, score: 1 };
        if (m.keywords.some((k) => k.startsWith(ql))) return { m, score: 2 };
        if (m.keywords.some((k) => k.includes(ql))) return { m, score: 3 };
        return { m, score: 4 };
      })
      .sort((a, b) => a.score - b.score)
      .slice(0, 6)
      .map((x) => x.m);
  }, [q, isAdmin, user]);

  // Build a flat "navigable" list for keyboard navigation
  const flatItems = useMemo(() => {
    const items = [];
    modulesFound.forEach((m) => items.push({ kind: "module", path: m.path, id: m.path, label: m.label }));
    results.pedidos.forEach((p) => items.push({ kind: "pedido", path: `/pedidos?highlight=${p.id}`, id: p.id, label: `${p.numero || p.id.slice(0,8)} — ${p.cliente_nombre || "Sin cliente"}`, meta: p }));
    results.productos.forEach((p) => items.push({ kind: "producto", path: `/productos?highlight=${p.id}`, id: p.id, label: p.nombre }));
    results.clientes.forEach((c) => items.push({ kind: "cliente", path: `/clientes?highlight=${c.id}`, id: c.id, label: c.nombre }));
    return items;
  }, [modulesFound, results]);

  // Reset cursor when results change
  useEffect(() => { setCursor(0); }, [q, flatItems.length]);

  const go = (path) => { setOpen(false); setQ(""); navigate(path); };

  // Keyboard navigation inside the spotlight
  const onInputKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, Math.max(0, flatItems.length - 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => Math.max(0, c - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const hit = flatItems[cursor];
      if (hit) go(hit.path);
    }
  };

  // Auto-scroll active row into view
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector(`[data-flat-idx="${cursor}"]`);
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "nearest" });
    }
  }, [cursor, open]);

  const hasResults = flatItems.length > 0;

  return (
    <>
      <div className="h-16 flex items-center justify-between px-3 sm:px-6 md:px-10 py-3 sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <div className="flex items-center gap-2 min-w-0">
          {onOpenMobileNav && (
            <button
              onClick={onOpenMobileNav}
              className="md:hidden w-10 h-10 rounded-full hover:bg-accent flex items-center justify-center text-foreground/70 flex-shrink-0"
              data-testid="mobile-nav-open"
              aria-label="Abrir menú"
              title="Abrir menú de navegación"
            >
              <Menu className="w-5 h-5" />
            </button>
          )}
          <div className="min-w-0">
            <div className="flex items-baseline gap-2">
              <div className="text-sm font-semibold text-foreground truncate">{title}</div>
            </div>
            {subtitle && <div className="text-[11px] text-muted-foreground truncate hidden sm:block">{subtitle}</div>}
          </div>
        </div>

        <div className="flex items-center gap-1 sm:gap-2">
          <button
            onClick={() => { setOpen(true); setTimeout(() => document.getElementById("beem-spotlight-input")?.focus(), 30); }}
            className="hidden sm:flex items-center gap-2 h-10 pl-3.5 pr-2.5 rounded-full border border-border/60 bg-card/70 backdrop-blur-sm hover:bg-accent hover:border-primary/30 transition-all text-xs text-muted-foreground group"
            data-testid="spotlight-open"
            title="Buscar cualquier módulo, cliente, producto o pedido (Cmd+K)"
          >
            <Search className="w-3.5 h-3.5" />
            <span className="pr-8">Buscar en Beem...</span>
            <kbd className="ml-auto text-[9px] px-1.5 py-0.5 rounded-md bg-muted border border-border/40 font-mono flex items-center gap-0.5">
              <Command className="w-2.5 h-2.5" />K
            </kbd>
          </button>
          {/* Búsqueda solo icono en móvil */}
          <button
            onClick={() => { setOpen(true); setTimeout(() => document.getElementById("beem-spotlight-input")?.focus(), 30); }}
            className="sm:hidden w-10 h-10 rounded-full hover:bg-accent flex items-center justify-center text-foreground/70"
            data-testid="spotlight-open-mobile"
            aria-label="Buscar"
            title="Buscar"
          >
            <Search className="w-4 h-4" />
          </button>
          <ActivityFeed />
          <ConnectionBadge />
          <NotificationBell />
          <ThemeToggle />
        </div>
      </div>

      {/* Spotlight overlay */}
      {open && (
        <div
          className="fixed inset-0 z-[60] bg-foreground/40 backdrop-blur-sm flex items-start justify-center pt-[10vh]"
          onClick={() => setOpen(false)}
          data-testid="spotlight-overlay"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-2xl bg-card border border-border rounded-3xl shadow-soft-lift overflow-hidden animate-slide-up"
          >
            <div className="flex items-center gap-3 px-5 py-4 border-b border-border/60">
              <Search className="w-4 h-4 text-muted-foreground" />
              <input
                id="beem-spotlight-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={onInputKeyDown}
                placeholder="Busca por nombre, código (EF26-192), SKU, cliente, pedido..."
                className="flex-1 bg-transparent outline-none text-[15px] placeholder:text-muted-foreground/60"
                data-testid="spotlight-input"
                autoFocus
              />
              <kbd className="text-[10px] px-1.5 py-0.5 rounded-md bg-muted border border-border/40">ESC</kbd>
            </div>

            <div ref={listRef} className="max-h-[400px] overflow-y-auto py-2">
              {q.length < 1 && (
                <div className="px-5 py-8 text-center text-xs text-muted-foreground">
                  Escribe para buscar módulos, pedidos, clientes o productos. Usa <kbd className="px-1 rounded bg-muted text-[10px]">↑↓</kbd> y <kbd className="px-1 rounded bg-muted text-[10px]">Enter</kbd> para navegar.
                </div>
              )}

              {q.length >= 1 && !hasResults && !loading && (
                <div className="px-5 py-8 text-center text-xs text-muted-foreground" data-testid="spotlight-no-results">
                  Sin resultados para "{q}"
                </div>
              )}

              {/* Módulos */}
              {modulesFound.length > 0 && (
                <div>
                  <div className="px-5 pt-3 pb-1 text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Módulos</div>
                  {modulesFound.map((m, i) => {
                    const flatIdx = i;
                    const active = flatIdx === cursor;
                    const Icon = m.icon;
                    return (
                      <button key={m.path} data-flat-idx={flatIdx} onClick={() => go(m.path)}
                        onMouseEnter={() => setCursor(flatIdx)}
                        className={`w-full px-5 py-2.5 text-left flex items-center gap-3 group transition-colors ${active ? "bg-accent" : "hover:bg-accent/50"}`}
                        data-testid={`spot-mod-${m.path.replace(/\//g, "-") || "root"}`}>
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] text-foreground truncate font-medium">{m.label}</div>
                          <div className="text-[10px] text-muted-foreground truncate">{m.desc}</div>
                        </div>
                        <ArrowRight className={`w-3.5 h-3.5 text-muted-foreground transition-opacity ${active ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`} />
                      </button>
                    );
                  })}
                </div>
              )}

              {loading && <div className="px-5 py-3 text-xs text-muted-foreground">Buscando...</div>}

              {results.pedidos.length > 0 && (
                <div>
                  <div className="px-5 pt-3 pb-1 text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Pedidos</div>
                  {results.pedidos.map((p, i) => {
                    const flatIdx = modulesFound.length + i;
                    const active = flatIdx === cursor;
                    const estado = (p.estado || "borrador").toLowerCase();
                    const estadoBadge = estado === "confirmado" ? "bg-emerald-500/15 text-emerald-700" :
                                        estado === "despachado" ? "bg-blue-500/15 text-blue-700" :
                                        estado === "entregado" ? "bg-primary/15 text-primary" :
                                        estado === "cancelado" ? "bg-rose-500/15 text-rose-700" :
                                        "bg-muted text-muted-foreground";
                    return (
                      <button key={p.id} data-flat-idx={flatIdx} onClick={() => go(`/pedidos?highlight=${p.id}`)}
                        onMouseEnter={() => setCursor(flatIdx)}
                        className={`w-full px-5 py-2 text-left flex items-center gap-3 group transition-colors ${active ? "bg-accent" : "hover:bg-accent/50"}`}
                        data-testid={`spot-ped-${p.id}`}>
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                          <ShoppingCart className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] text-foreground truncate font-medium">{p.numero || p.id.slice(0, 8)} · {p.cliente_nombre || "Sin cliente"}</div>
                          <div className="text-[10px] text-muted-foreground flex items-center gap-2">
                            <span className={`px-1.5 py-0.5 rounded-full text-[9px] uppercase tracking-wide ${estadoBadge}`}>{estado}</span>
                            <span>${(p.total || 0).toLocaleString("es-CO")}</span>
                            {p.numero_remision && <span>· Rem {p.numero_remision}</span>}
                          </div>
                        </div>
                        <ArrowRight className={`w-3.5 h-3.5 text-muted-foreground transition-opacity ${active ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`} />
                      </button>
                    );
                  })}
                </div>
              )}

              {results.productos.length > 0 && (
                <div>
                  <div className="px-5 pt-3 pb-1 text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Productos</div>
                  {results.productos.map((p, i) => {
                    const flatIdx = modulesFound.length + results.pedidos.length + i;
                    const active = flatIdx === cursor;
                    return (
                      <button key={p.id} data-flat-idx={flatIdx} onClick={() => go(`/productos?highlight=${p.id}`)}
                        onMouseEnter={() => setCursor(flatIdx)}
                        className={`w-full px-5 py-2 text-left flex items-center gap-3 group transition-colors ${active ? "bg-accent" : "hover:bg-accent/50"}`}
                        data-testid={`spot-prod-${p.id}`}>
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary text-xs font-mono">
                          {(p.codigo || p.sku || "PR").slice(0, 2)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] text-foreground truncate">
                            {p.codigo && (
                              <span className="mr-1.5 px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 text-[10px] font-mono font-bold" title="Código real">
                                {p.codigo}
                              </span>
                            )}
                            {p.nombre}
                          </div>
                          <div className="text-[10px] text-muted-foreground">
                            Stock {p.stock_actual ?? 0}
                          </div>
                        </div>
                        <ArrowRight className={`w-3.5 h-3.5 text-muted-foreground transition-opacity ${active ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`} />
                      </button>
                    );
                  })}
                </div>
              )}
              {results.clientes.length > 0 && (
                <div>
                  <div className="px-5 pt-3 pb-1 text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Clientes</div>
                  {results.clientes.map((c, i) => {
                    const flatIdx = modulesFound.length + results.pedidos.length + results.productos.length + i;
                    const active = flatIdx === cursor;
                    return (
                      <button key={c.id} data-flat-idx={flatIdx} onClick={() => go(`/clientes?highlight=${c.id}`)}
                        onMouseEnter={() => setCursor(flatIdx)}
                        className={`w-full px-5 py-2 text-left flex items-center gap-3 group transition-colors ${active ? "bg-accent" : "hover:bg-accent/50"}`}
                        data-testid={`spot-cli-${c.id}`}>
                        <div className="w-8 h-8 rounded-lg bg-secondary/20 flex items-center justify-center text-primary text-xs font-display italic">
                          {(c.nombre || "?").slice(0, 1).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] text-foreground truncate">{c.nombre}</div>
                          <div className="text-[10px] text-muted-foreground truncate">{c.zona || "Sin zona"} · {c.telefono || "Sin tel"}</div>
                        </div>
                        <ArrowRight className={`w-3.5 h-3.5 text-muted-foreground transition-opacity ${active ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`} />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="px-5 py-2.5 border-t border-border/60 bg-muted/30 text-[10px] text-muted-foreground flex items-center justify-between">
              <span>Enter para abrir · ↑↓ para navegar</span>
              <span className="flex items-center gap-1">Impulsado por <span className="font-display italic text-primary">beem</span></span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
